import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { MultiValueGenericProps } from 'react-select';
import { Select } from '~/components/Select';
import type { IcdCodeOptionType } from '~/components/Patient/Referrals/ReferralInfo/ReferralInfo';
import { useReferralService } from '~/services/ReferralService';
import { IcdCodeType } from '~/services/ReferralService/useGetIcdCodes';
import { getDiagnosisCodeLabel, togglePrimaryDiagnosisCode } from '~/util/diagnosisCodes';

const ICD_CODE_SEARCH_DEBOUNCE_MS = 250;
const ICD_CODE_SEARCH_PAGE_ROWS = 200;

export interface DiagnosisCodePickerProps {
    inputId: string;
    value: IcdCodeOptionType[];
    onChange: (selectedCodes: IcdCodeOptionType[]) => void;
}

export function DiagnosisCodePicker({ inputId, value, onChange }: DiagnosisCodePickerProps) {
    const { getIcdCodes } = useReferralService();
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>();

    const togglePrimary = (code: string) => onChange(togglePrimaryDiagnosisCode(value, code));
    // react-select remounts a custom component whenever its identity changes, so the
    // chip component below stays stable and reads the latest handler from this ref.
    const togglePrimaryRef = useRef(togglePrimary);
    togglePrimaryRef.current = togglePrimary;

    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    const loadIcdCodes = useCallback(
        (searchTerm: string, callback: (options: IcdCodeOptionType[]) => void) => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }

            debounceTimerRef.current = setTimeout(async () => {
                try {
                    const result = await getIcdCodes({
                        q: searchTerm,
                        page_rows: ICD_CODE_SEARCH_PAGE_ROWS,
                    });
                    const options = result.icd_codes.map((icdCode: IcdCodeType) => ({
                        value: icdCode.id,
                        label: getDiagnosisCodeLabel(icdCode),
                        code: icdCode.code,
                        description: icdCode.description,
                        primary: false,
                    }));
                    callback(options);
                } catch (error) {
                    callback([]);
                }
            }, ICD_CODE_SEARCH_DEBOUNCE_MS);
        },
        [getIcdCodes]
    );

    const selectComponents = useMemo(
        () => ({
            MultiValueLabel: ({
                children,
                data,
                innerProps,
            }: MultiValueGenericProps<IcdCodeOptionType, true>) => (
                <div className={innerProps.className}>
                    <button
                        type="button"
                        className={`primary-toggle ${data.primary ? 'is-primary' : ''}`}
                        aria-pressed={data.primary}
                        title={
                            data.primary
                                ? 'Primary diagnosis code. Click to clear it.'
                                : 'Make this the primary diagnosis code'
                        }
                        aria-label={
                            data.primary
                                ? `${data.code} is the primary diagnosis code. Click to clear it.`
                                : `Make ${data.code} the primary diagnosis code`
                        }
                        onMouseDown={(event) => event.stopPropagation()}
                        onClick={() => togglePrimaryRef.current(data.code)}
                    >
                        <i className="fa-solid fa-star" aria-hidden="true" />
                    </button>
                    {children}
                </div>
            ),
        }),
        []
    );

    return (
        <div className="diagnosis-code-picker">
            <Select<IcdCodeOptionType, true>
                async
                isMulti
                isClearable
                menuPosition="fixed"
                inputId={inputId}
                label="Diagnosis Code"
                classNamePrefix="diagnosis-code-picker"
                value={value}
                components={selectComponents}
                loadOptions={loadIcdCodes}
                getOptionLabel={getDiagnosisCodeLabel}
                getOptionValue={(diagnosis) => diagnosis.code}
                onChange={(selectedCodes) => onChange(selectedCodes ? [...selectedCodes] : [])}
                placeholder="Search by ICD-10 code or description"
                noOptionsMessage={({ inputValue }) =>
                    inputValue ? 'No results found' : 'No results yet. Start typing to search.'
                }
            />
        </div>
    );
}
